import React, { useContext, useState } from 'react'
import { PromozioneNode, RigaOrdineNode } from '@gql/graphql'
import styles from './RigaOrdineCard.module.sass'
import useTrans from '@hooks/useTrans'
import { Button, Modal, Tag, WImage } from '@components/atoms'
import { prezziProdotto, priceNotation } from '@utils/safe'
import useProdotti from '@hooks/useProdotti'
import { CartContext } from '@utils/cartContext'
import { handleAddToCartTracking, handleBuyAgainTracking } from '@utils/tracking'

interface Props {
  rigaOrdine?: Partial<RigaOrdineNode>
  className?: string
  variant?: 'fill' | 'transparent'
  type?: 'default' | 'riepilogo-ordine'
  promozioneApplicata?: PromozioneNode
}

const RigaOrdineCard = (props: Props) => {
  const {
    rigaOrdine = undefined,
    className = '',
    type = 'default',
    variant = 'fill',
    promozioneApplicata = undefined,
  } = props

  const [openModal, setOpenModal] = useState(false)

  const t = useTrans()

  const { prodotti, loading: loadingProdottoExtraFields } = useProdotti([
    rigaOrdine?.prodotto?.prodotto,
  ])

  const { cart, addToCart: contextAddToCart } = useContext(CartContext)

  const pp = prezziProdotto(rigaOrdine?.prodotto?.prodotto)
  const prezzi = [
    { key: 'listino', label: t('Listino'), value: pp?.prezzoListino },
    {
      key: 'current',
      label: rigaOrdine?.prodotto?.prodotto?.promo
        ? t('Promo')
        : rigaOrdine?.prodotto?.prodotto?.lancio
        ? t('Prezzo lancio')
        : '',
      value: pp?.prezzoFinale,
    },
  ].filter((x) => !!x.value)

  const taglieOpts = prodotti[0]?.prodottiTendina?.map((x) => ({
    label: x.labelTendina,
    value: x.pk,
  }))
  const tagliaProdotto = rigaOrdine?.prodotto?.prodotto?.pk

  const addToCart = () => {
    if (!prodotti || !prodotti[0].pk) return

    contextAddToCart(prodotti[0].pk, rigaOrdine.quantitaOrdinata, true)
    handleAddToCartTracking(prodotti[0], cart, 'acquista_di_nuovo')
    setOpenModal(false)
  }

  return rigaOrdine && rigaOrdine.prodotto && prodotti[0] ? (
    <div
      className={`${styles.root} ${
        variant === 'transparent' ? styles['root--transparent'] : ''
      } ${className}`}
    >
      <div className={styles.imageBox}>
        <WImage src={prodotti[0].previewImage} maxWidth={64} maxHeight={64} objectFit="contain" />
      </div>
      <div className={styles.main}>
        <div className={`${styles.content} ${styles[`content--${type}`]}`}>
          {prodotti[0].categoria && (
            <span className={styles.category}>{prodotti[0].categoria.boxTitolo}</span>
          )}
          <div className={styles.wrapper}>
            <div className={styles.left}>
              <h2 className={styles.title}>{prodotti[0].nome}</h2>
              <div className={styles.attributes}>
                <div className={styles.attribute}>
                  <span className={styles.attributeLabel}>{t('Quantità')}: </span>
                  <span className={styles.attributeValue}>{rigaOrdine.quantitaOrdinata}</span>
                </div>
                {(prodotti[0].showTaglia || prodotti[0].showFormato) && (
                  <div className={styles.attribute}>
                    <span className={styles.attributeLabel}>
                      {t(prodotti[0].showTaglia ? 'Taglia' : 'Formato')}:
                    </span>
                    <span className={styles.attributeValue}>
                      {taglieOpts.filter((x) => x.value === tagliaProdotto)?.[0]?.label}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {type === 'riepilogo-ordine' && (
              <div className={styles.right}>
                <div className={styles.prices}>
                  {prodotti[0].promo && pp.prezzoPrima && (
                    <div>
                      <div className={`${styles.price}`}>
                        {<span className={styles.priceLabel}>{t('Prima era')} </span>}
                        <span className={styles.priceValue}>
                          {priceNotation(pp.prezzoPrima, false, prodotti[0].valuta?.simbolo)}
                        </span>
                      </div>
                    </div>
                  )}

                  {pp.prezzoListino && (
                    <div>
                      <div className={`${styles.price}`}>
                        {<span className={styles.priceLabel}>{t('Listino')} </span>}
                        <span className={styles.priceValue}>
                          {priceNotation(pp.prezzoListino, false, prodotti[0].valuta?.simbolo)}
                        </span>
                      </div>
                    </div>
                  )}

                  {pp.prezzoFinale && (
                    <div>
                      <div className={`${styles.priceCurrent}`}>
                        <span className={styles.priceValue}>
                          {priceNotation(pp.prezzoFinale, false, prodotti[0].valuta?.simbolo)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                <Button
                  label={t('Acquista di nuovo')}
                  onClick={() => {
                    setOpenModal(true)
                    handleBuyAgainTracking(prodotti[0].codice)
                  }}
                  variant="ghost"
                  className={styles.cta}
                  size="sm"
                />
              </div>
            )}
          </div>
        </div>
        {rigaOrdine.omaggio ? (
          <Tag label={t('In omaggio')} variant="promo" className={styles.tag} />
        ) : (
          <div className={`${styles.pricesBox} ${styles[`pricesBox--${type}`]}`}>
            {!!prezzi?.length && (
              <div className={styles.prices}>
                {prezzi.map((p) => (
                  <div className={`${styles.price} ${styles[`price--${p.key}`]}`} key={p.key}>
                    {!!p.label && <span className={styles.priceLabel}>{p.label}: </span>}
                    <span className={styles.priceValue}>
                      {priceNotation(p.value, false, prodotti[0].valuta?.simbolo)}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {promozioneApplicata?.promoDipendenti && (
              <Tag label={promozioneApplicata.titolo} size="sm" variant="black" />
            )}
          </div>
        )}
      </div>

      {type === 'riepilogo-ordine' && (
        <Modal
          id="acquista_di_nuovo_popup"
          open={openModal}
          onClose={() => setOpenModal(false)}
          title={t('Vuoi acquistare di nuovo questo prodotto?')}
          className={styles.modal}
          children={
            <div className={styles.description}>
              {rigaOrdine?.prezzoUnitarioScontato !== pp?.prezzoFinale && (
                <div className={styles.popupPriceBox}>
                  {t(`Precedentemente lo hai acquistato a`)}{' '}
                  <span>
                    {priceNotation(
                      rigaOrdine.prezzoUnitarioScontato,
                      false,
                      prodotti[0].valuta?.simbolo
                    )}
                    .
                  </span>
                  <div>
                    {t('Ora costa')}{' '}
                    <span>
                      {priceNotation(pp.prezzoFinale, false, prodotti[0].valuta?.simbolo)}.
                    </span>
                  </div>
                </div>
              )}
            </div>
          }
          cta={
            <div className={styles.popupButtons}>
              <Button label={t('Si, aggiungi al carrello')} size="lg" onClick={addToCart} />
              <Button
                variant="secondary"
                label={t('No, annulla')}
                onClick={() => setOpenModal(false)}
                size="lg"
              />
            </div>
          }
        />
      )}
    </div>
  ) : (
    <></>
  )
}

export default RigaOrdineCard
